/**
 * Pulse title link detection.
 *
 * When a pulse title contains a URL we split the title at the exact
 * match position so the timeline can render `{before}<a>{label}<icon/></a>{after}`
 * inline. The friendly label override is persisted on
 * `pulses.metadata.linkTitle`; the URL itself stays in the
 * `pulseDescription` text so existing search / linkify paths continue
 * to see it.
 *
 * The matcher accepts both `https?://…` and bare `www.…` links. For
 * bare `www.` matches the rendered `<a href>` is normalised by
 * prepending `https://` so clicking actually works.
 */

// The `(?<![@A-Za-z0-9])` lookbehind on the `www.` branch prevents the
// regex from grabbing the host portion of an email address like
// `foo@www.example.com` — without it, the `\b` boundary at `@` would
// happily match `www.example.com` and we'd render a misleading link.
const URL_REGEX = /(?:\bhttps?:\/\/[^\s<>"')]+|(?<![@A-Za-z0-9])www\.[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+[^\s<>"')]*)/i;
const TRAILING_PUNCT = /[)\].,;:!?]+$/;

export interface UrlMatch {
  /** The URL exactly as it appears inside the title text (after trailing-punctuation trim). */
  url: string;
  /** The URL with `https://` prepended if it was missing — safe to put in an `<a href>`. */
  hrefUrl: string;
  /** Start index of the match inside the source text. */
  index: number;
  /** Exclusive end index of the match inside the source text. */
  endIndex: number;
}

export function findFirstUrl(text: string | null | undefined): UrlMatch | null {
  if (!text) return null;
  const m = text.match(URL_REGEX);
  if (!m || m.index === undefined) return null;
  const raw = m[0];
  const trimmed = raw.replace(TRAILING_PUNCT, '');
  const hrefUrl = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  return {
    url: trimmed,
    hrefUrl,
    index: m.index,
    endIndex: m.index + trimmed.length,
  };
}

/** Back-compat helper — returns just the URL substring or null. */
export function detectFirstUrl(text: string | null | undefined): string | null {
  return findFirstUrl(text)?.url ?? null;
}

/**
 * Reduce a string that may contain HTML to clean, single-line plain text.
 *
 * Used as a defensive guard where a pulse title is built from
 * `pulseDescription` — legacy/bad rows (e.g. Word/Outlook emails whose
 * "plain text" part was actually HTML) can carry raw markup like
 * `<div class="WordSection1"><p class="MsoNormal">…`. Rendering that verbatim
 * makes the timeline unreadable and triggers forced-reflow warnings.
 *
 * This is a PURE string transform — it never assigns to the DOM
 * (`innerHTML` / `DOMParser`), which would be an XSS risk for untrusted HTML.
 * It strips `<script>`/`<style>` blocks, removes remaining tags, decodes the
 * common HTML entities, and collapses whitespace.
 */
const HTML_ENTITY_MAP: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
};

export function stripHtmlToText(input: string | null | undefined): string {
  if (!input) return '';
  // Fast path: nothing that could be a tag or an entity.
  if (!/[<&]/.test(input)) return input;

  let text = input
    // Drop script/style blocks (content and all) before generic tag removal.
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ')
    // Remove every remaining tag.
    .replace(/<[^>]*>/g, ' ');

  // Decode entities AFTER tags are gone, so a decoded "<" is literal text and
  // can never re-introduce a tag.
  text = text
    .replace(/&#(\d+);/g, (_, dec: string) => {
      const code = parseInt(dec, 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : _;
    })
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => {
      const code = parseInt(hex, 16);
      return Number.isFinite(code) ? String.fromCodePoint(code) : _;
    })
    .replace(/&(?:nbsp|amp|lt|gt|quot|apos|#39);/gi, (m) => HTML_ENTITY_MAP[m.toLowerCase()] ?? m);

  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Replace every `<img>` whose `src` is a `cid:` reference with an inert
 * placeholder span before the HTML is rendered.
 *
 * Inline `cid:` images that were never resolved to a hosted URL are blocked by
 * the page's `img-src` CSP, flooding the console with errors when the pulse is
 * opened. Stripping them here (defensively, in addition to the server-side
 * persist pass) keeps legacy rows that still carry `cid:` refs quiet. Only
 * `cid:` sources are touched — `https:`/`data:`/`blob:` images render normally.
 *
 * Pure string transform; the result is still passed through DOMPurify by the
 * caller before reaching the DOM.
 */
export function neutralizeCidImagesHtml(html: string | null | undefined): string {
  if (!html) return '';
  if (!/src\s*=\s*['"]?\s*cid:/i.test(html)) return html;
  return html.replace(
    /<img\b[^>]*\bsrc\s*=\s*(?:(['"])\s*cid:[^'"]*\1|cid:[^\s>]+)[^>]*>/gi,
    '<span class="email-image-failed" title="Inline image unavailable">[Image unavailable]</span>',
  );
}

export interface PulseTitleParts {
  before: string;
  url: string;
  hrefUrl: string;
  /** What to render as the link's visible text — friendly label if set, else the URL. */
  label: string;
  after: string;
}

/**
 * Split a pulse title around its first URL match. Returns `null` when
 * the title contains no URL.
 *
 * `label` honours `metadata.linkTitle` (a non-empty trimmed string)
 * when present, otherwise it falls back to the raw URL so the user
 * still sees something clickable.
 */
export function getPulseTitleParts(
  baseLabel: string,
  metadata: Record<string, unknown> | null | undefined,
): PulseTitleParts | null {
  const m = findFirstUrl(baseLabel);
  if (!m) return null;
  const raw = metadata && typeof metadata.linkTitle === 'string' ? metadata.linkTitle.trim() : '';
  return {
    before: baseLabel.slice(0, m.index),
    url: m.url,
    hrefUrl: m.hrefUrl,
    label: raw || m.url,
    after: baseLabel.slice(m.endIndex),
  };
}
